import React from "react";
import SiteShell from "@/components/SiteShell";

/** Terms of Service (www.frazzlecrm.com/terms). Drafted for launch — review before major changes. */

const EFFECTIVE = "July 19, 2026";

function H({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-8 text-[17px] font-bold text-slate-900">{children}</h2>
  );
}

export default function Terms() {
  return (
    <SiteShell title="Terms of Service — FrazzleCRM">
      <section className="mx-auto max-w-3xl px-6 pb-20 pt-16">
        <h1 className="text-[32px] font-extrabold tracking-tight text-slate-900">
          Terms of Service
        </h1>
        <p className="mt-2 text-[13px] text-slate-400">
          Effective {EFFECTIVE}
        </p>
        <div className="mt-6 space-y-4 text-[14.5px] leading-relaxed text-slate-600">
          <p>
            These Terms govern your use of FrazzleCRM (&ldquo;the
            Service&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) at
            frazzlecrm.com and app.frazzlecrm.com. By creating an account or
            using the Service you agree to them. If you use the Service on
            behalf of a business, you represent that you can bind that
            business to these Terms.
          </p>

          <H>1. The Service</H>
          <p>
            FrazzleCRM is a CRM, funnel-mapping, and marketing-automation
            platform. You connect your own sending infrastructure (email
            credentials, SMS providers, ad accounts); messages sent through
            the Service are sent by you, from your accounts, to your
            contacts.
          </p>

          <H>2. Your account</H>
          <p>
            You are responsible for your account credentials and for all
            activity under your workspaces. Keep your password secure and
            tell us promptly at support@frazzlecrm.com if you suspect
            unauthorized access.
          </p>

          <H>3. Acceptable use</H>
          <p>
            You agree not to use the Service to send spam or messages that
            violate applicable law (including CAN-SPAM, TCPA, and GDPR/CCPA
            obligations that apply to you); to store or transmit malware or
            unlawful content; to infringe others&apos; rights; or to probe,
            disrupt, or overload the Service. You are responsible for
            obtaining any consents required to contact the people in your
            CRM. We may suspend accounts that violate this section.
          </p>

          <H>4. Plans, billing, and cancellation</H>
          <p>
            Paid plans are billed in advance, monthly or annually, through
            Stripe. Plan limits are described on the pricing page.
            Subscriptions renew automatically until cancelled; when you
            cancel, your plan stays active until the end of the paid period
            and then downgrades to Free — your data stays where it is, and
            anything over Free-plan limits keeps running but new creation is
            blocked until you&apos;re back under the limits or upgrade. Except
            where required by law, payments are non-refundable. We may change
            prices with at least 30 days&apos; notice, applying at your next
            renewal.
          </p>

          <H>5. Your data</H>
          <p>
            You own the content and contact data you put into the Service.
            You grant us the rights needed to host, process, and transmit it
            solely to operate the Service. We handle personal data as
            described in our <a href="/privacy" className="text-indigo-600 hover:underline">Privacy Policy</a>. You are the data controller for
            your contacts; we process that data on your instructions.
          </p>

          <H>6. Availability and support</H>
          <p>
            We work hard to keep the Service up but provide it &ldquo;as
            is&rdquo; and &ldquo;as available&rdquo;, without warranties of
            any kind, express or implied. Support is provided by email at
            support@frazzlecrm.com.
          </p>

          <H>7. Limitation of liability</H>
          <p>
            To the maximum extent permitted by law, we are not liable for
            indirect, incidental, special, consequential, or punitive
            damages, or lost profits, revenue, or data. Our total liability
            for any claim relating to the Service is limited to the amounts
            you paid us in the 12 months before the claim arose.
          </p>

          <H>8. Termination</H>
          <p>
            You can delete your account at any time. We may suspend or
            terminate accounts that materially breach these Terms; where
            practical we&apos;ll notify you first and give you a chance to
            export your data.
          </p>

          <H>9. Changes to these Terms</H>
          <p>
            We may update these Terms; material changes will be announced on
            this page (and by email for paying customers) at least 14 days
            before they take effect. Continuing to use the Service after
            changes take effect means you accept them.
          </p>

          <H>10. Contact</H>
          <p>
            Questions about these Terms: support@frazzlecrm.com.
          </p>
        </div>
      </section>
    </SiteShell>
  );
}
